import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, Lock, User, Phone, Building2, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/apiError";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailExists, setEmailExists] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setEmailExists(false);
    setLoading(true);
    try {
      await register(form);
      trackEvent("konto_skapat");
      toast.success("Konto skapat");
      nav("/dashboard");
    } catch (err) {
      const status = err?.response?.status;
      const msg = formatApiError(err, "Registrering misslyckades. Kontrollera dina uppgifter och försök igen.");
      if (status === 400 && /redan registrerad/i.test(msg)) {
        setEmailExists(true);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const f = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (error) setError(null);
    if (emailExists) setEmailExists(false);
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-6 py-12">
      <Seo
        title="Skapa konto"
        description="Skapa ett kostnadsfritt konto på Bygglovskompassen för att spara dina bygglovsärenden, ladda upp ritningar och få personlig AI-rådgivning."
        path="/register"
      />
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-sm p-8 animate-fade-in-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-blue-700 text-white flex items-center justify-center rounded-sm">
            <Building2 className="w-5 h-5 stroke-[1.5]" />
          </div>
          <div>
            <div className="text-lg font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>Bygglovskompassen</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Skapa konto</div>
          </div>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
          Skapa konto på Bygglovskompassen
        </h1>
        <p className="mt-1 text-sm text-zinc-500">Skapa ett kostnadsfritt konto som privatperson eller företag. Spara dina ärenden och ladda upp ritningar.</p>

        {error && (
          <div
            data-testid="reg-error"
            className="mt-5 flex items-start gap-2 border border-red-200 bg-red-50 text-red-800 rounded-sm px-3 py-2.5 text-sm"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 stroke-[1.75]" />
            <div className="space-y-1.5">
              <div>{error}</div>
              {emailExists && (
                <Link
                  to={`/login?email=${encodeURIComponent(form.email)}`}
                  data-testid="reg-error-login-link"
                  className="inline-flex items-center gap-1 font-medium text-red-900 underline hover:text-red-950"
                >
                  Logga in istället <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-3" noValidate>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input data-testid="reg-name" required value={form.name} onChange={f("name")} placeholder="För- och efternamn"
              className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input data-testid="reg-email" type="email" required value={form.email} onChange={f("email")} placeholder="E-post"
              className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
          </div>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input data-testid="reg-phone" value={form.phone} onChange={f("phone")} placeholder="Telefon (valfritt)"
              className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={f("password")} placeholder="Lösenord (min 6 tecken)"
              className="w-full pl-10 pr-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-sm" />
          </div>
          <button
            type="submit"
            data-testid="reg-submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-medium py-3 rounded-sm transition active:scale-[0.98] disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Skapa konto <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <p className="mt-6 text-sm text-zinc-500 text-center">
          Har du redan ett konto?{" "}
          <Link to="/login" data-testid="link-login" className="text-blue-700 hover:underline font-medium">Logga in</Link>
        </p>
      </div>
    </div>
  );
}
